import {
  createContext,
  useContext,
  useId,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react'
import { Eye, EyeOff, TriangleAlert } from 'lucide-react'
import { cn } from '@/lib/utils'

import { Input as ShadInput } from './input'
import { Textarea as ShadTextarea } from './textarea'

/**
 * Form controls.
 *
 * These wrap shadcn's primitives, which are built on Base UI. Three things are
 * enforced here rather than being remembered at ~40 call sites:
 *
 * 1. **A 44px touch target.** shadcn's input is `h-8`. This app is used
 *    one-handed on a phone, next to a running machine, often with gloves.
 *
 * 2. **Real label association.** See the note on `Field`.
 *
 * 3. **Invalid state actually showing.** shadcn's input already ships
 *    `aria-invalid:border-destructive aria-invalid:ring-destructive/20`, but
 *    nothing was ever setting `aria-invalid`, so a rejected field showed red
 *    text underneath while the box itself looked perfectly fine. `Field` now
 *    wires it up.
 */

// ---------------------------------------------------------------------------
// Field
// ---------------------------------------------------------------------------

interface FieldContextValue {
  controlId: string
  describedBy?: string
  invalid: boolean
  required: boolean
}

const FieldContext = createContext<FieldContextValue | null>(null)

/**
 * Label + control + hint + error.
 *
 * **This used to wrap everything in a `<label>`**, which associates the label
 * implicitly and looks tidy, but has a real cost: every piece of text inside a
 * `<label>` becomes part of the control's accessible name. A screen reader
 * announced the employee-code box as
 *
 *   "Šifra zaposlenog * Ovim se korisnik prijavljuje u aplikaciju."
 *
 * and, once the server rejected something, the error was glued onto the end of
 * the name too. Hints and errors belong in `aria-describedby`, which is
 * announced *after* the name and re-announced when it changes.
 *
 * So the label is now explicit (`htmlFor`) and the control picks up its id,
 * description and invalid state from context. Every control below reads that
 * context; anything else placed in a `Field` should be given `id={...}` to
 * match, or the label will point at nothing.
 */
export function Field({
  label,
  hint,
  error,
  required,
  action,
  children,
}: {
  label: string
  hint?: string
  error?: string
  required?: boolean
  /**
   * A control that belongs to this field but is not part of it — today only
   * "forgot your password?" beside the password label.
   *
   * It sits on the label's own line rather than under the input, which is the
   * convention and, on the login screen, the difference between the sign-in
   * button being visible and the page needing a scroll. Kept outside the
   * `<label>` so it never becomes part of the control's accessible name; see
   * the note above for why that matters.
   */
  action?: ReactNode
  children: ReactNode
}) {
  const uid = useId()
  const controlId = `${uid}-control`
  const hintId = `${uid}-hint`
  const errorId = `${uid}-error`

  /*
   * The hint stays visible alongside the error. They answer different
   * questions — "najmanje 8 znakova" is the rule, "lozinka je prekratka" is
   * what went wrong — and hiding the rule at the moment it is broken is
   * exactly backwards.
   */
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ')

  return (
    <FieldContext.Provider
      value={{
        controlId,
        describedBy: describedBy || undefined,
        invalid: Boolean(error),
        required: Boolean(required),
      }}
    >
      <div className="block">
        <div className="mb-1.5 flex items-baseline justify-between gap-3">
          <label htmlFor={controlId} className="block text-sm font-medium">
            {label}
            {required && (
              <span className="text-destructive ml-0.5" title="Obavezno polje">
                *
              </span>
            )}
          </label>

          {action}
        </div>

        {children}

        {hint && (
          <span id={hintId} className="muted mt-1 block text-xs">
            {hint}
          </span>
        )}

        {/* role=alert so a validation failure is announced, not just coloured. */}
        {error && (
          <span
            id={errorId}
            role="alert"
            className="text-destructive mt-1 block text-xs font-medium"
          >
            {error}
          </span>
        )}
      </div>
    </FieldContext.Provider>
  )
}

/**
 * Merges what a `Field` knows with what the call site passed.
 *
 * An explicit prop always wins, so a control can still be used outside a Field
 * or override a single attribute.
 */
function useFieldProps(own: {
  id?: string
  describedBy?: string
  invalid?: boolean
  required?: boolean
}) {
  const field = useContext(FieldContext)

  const invalid = own.invalid ?? field?.invalid ?? false

  return {
    id: own.id ?? field?.controlId,
    // Only ever "true" or absent. aria-invalid="false" is legal but Tailwind's
    // aria-invalid: variant keys on the literal "true", so an explicit false
    // would be dead weight in the DOM.
    'aria-invalid': invalid || undefined,
    'aria-describedby': own.describedBy ?? field?.describedBy,
    'aria-required': (own.required ?? field?.required) || undefined,
  }
}

// ---------------------------------------------------------------------------
// Text-ish inputs
// ---------------------------------------------------------------------------

export function Input({
  className,
  id,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  required,
  ...props
}: ComponentProps<typeof ShadInput>) {
  const aria = useFieldProps({
    id,
    describedBy,
    invalid: typeof invalid === 'boolean' ? invalid : undefined,
    required,
  })

  return (
    <ShadInput
      required={required}
      className={cn('min-h-11', FIELD_SURFACE, className)}
      {...aria}
      {...props}
    />
  )
}

/**
 * The iOS text-field look, shared by every control.
 *
 * Filled rather than outlined. On a phone a hairline border around a white box
 * is nearly invisible in daylight, and the whole point of a field is that you
 * can see where to tap; a filled shape reads as a target from across a
 * workbench. The fill drops away on focus so the caret sits on plain paper,
 * which is what the platform does.
 */
const FIELD_SURFACE = cn(
  'rounded-xl border-transparent bg-muted/60 transition-colors',
  'focus-visible:border-ring focus-visible:bg-background',
  // The invalid tint has to beat the fill, or a rejected field looks normal.
  'aria-invalid:border-destructive aria-invalid:bg-destructive/5',
)

export function Textarea({
  className,
  id,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  required,
  ...props
}: ComponentProps<typeof ShadTextarea>) {
  const aria = useFieldProps({
    id,
    describedBy,
    invalid: typeof invalid === 'boolean' ? invalid : undefined,
    required,
  })

  return (
    <ShadTextarea
      required={required}
      className={cn('min-h-24', FIELD_SURFACE, className)}
      {...aria}
      {...props}
    />
  )
}

/**
 * A native `<select>`, on purpose.
 *
 * shadcn ships a rich Select built on Base UI and on a desktop it is nicer. On
 * a phone the native control opens the OS picker — full height, thumb-operable,
 * readable through a glove — where the custom one renders a small floating
 * listbox that has to be scrolled precisely. The primary user here is standing
 * at a machine, so the native control wins.
 *
 * The invalid styling is spelled out because this is not shadcn's Input and
 * does not inherit its `aria-invalid:` rules.
 */
export function Select({
  className,
  children,
  id,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  required,
  ...props
}: ComponentProps<'select'>) {
  const aria = useFieldProps({
    id,
    describedBy,
    invalid: typeof invalid === 'boolean' ? invalid : undefined,
    required,
  })

  return (
    <select
      required={required}
      className={cn(
        'min-h-11 w-full border px-3 py-2 text-base md:text-sm',
        'focus-visible:ring-ring/50 focus-visible:ring-3',
        'disabled:cursor-not-allowed disabled:opacity-60',
        'aria-invalid:ring-destructive/20 aria-invalid:ring-3',
        FIELD_SURFACE,
        className,
      )}
      {...aria}
      {...props}
    >
      {children}
    </select>
  )
}

// ---------------------------------------------------------------------------
// Checkbox
// ---------------------------------------------------------------------------

/**
 * A checkbox with its label, sized for a thumb.
 *
 * A bare `<input type="checkbox">` is about 13px — a third of the 44px this app
 * holds itself to everywhere else, and the hardest control to hit of the lot.
 * Wrapping it in the `<label>` makes the whole row the target, so the text is
 * as tappable as the box.
 *
 * Native rather than shadcn's Checkbox: `accent-color` gets the brand colour
 * onto the real control, which keeps the OS's own focus, hover and
 * high-contrast handling instead of reimplementing them.
 */
export function Checkbox({
  label,
  hint,
  className,
  ...props
}: Omit<ComponentProps<'input'>, 'type'> & { label: ReactNode; hint?: ReactNode }) {
  return (
    <label className="flex min-h-11 cursor-pointer items-start gap-3 py-1.5">
      <input
        type="checkbox"
        className={cn(
          'accent-primary mt-0.5 size-5 shrink-0 cursor-pointer',
          'focus-visible:ring-ring/50 focus-visible:ring-3 focus-visible:outline-none',
          className,
        )}
        {...props}
      />
      <span className="text-sm leading-snug">
        {label}
        {hint && <span className="muted mt-0.5 block text-xs">{hint}</span>}
      </span>
    </label>
  )
}

// ---------------------------------------------------------------------------
// Password
// ---------------------------------------------------------------------------

/**
 * A password box with a reveal toggle and a Caps Lock warning.
 *
 * Both exist for the same reason. The people signing in are on the shop floor,
 * on a phone, frequently with gloves on, and their password was handed to them
 * verbally by a supervisor (see the reset-password flow — most workers have no
 * e-mail, so that is the only way they get one). Typing a password blind under
 * those conditions and being told only "pogrešna šifra ili lozinka" is a
 * genuinely bad time, and it is the single most common way someone gets locked
 * out of a tool they are required to use.
 *
 * Caps Lock cannot be read until a key is pressed — there is no way to query it
 * on focus — so the warning appears from the first keystroke.
 */
export function PasswordInput({
  className,
  onKeyDown,
  onKeyUp,
  onBlur,
  // Dots rather than words. A password box that says "Unesite lozinku" is
  // indistinguishable from one that has already been filled in, which is the
  // one thing the placeholder in this field has to get right.
  placeholder = '••••••••',
  ...props
}: ComponentProps<typeof ShadInput>) {
  const [visible, setVisible] = useState(false)
  const [capsLock, setCapsLock] = useState(false)

  function readCapsLock(event: React.KeyboardEvent<HTMLInputElement>) {
    // getModifierState is unavailable on some soft keyboards, where it simply
    // reports false — no warning shown, which is the right failure direction.
    setCapsLock(event.getModifierState?.('CapsLock') ?? false)
  }

  return (
    <>
      <div className="relative">
        <Input
          {...props}
          placeholder={placeholder}
          type={visible ? 'text' : 'password'}
          // Room for the toggle, so a long password does not run underneath it.
          className={cn('pr-12', className)}
          onKeyDown={(event) => {
            readCapsLock(event)
            onKeyDown?.(event)
          }}
          onKeyUp={(event) => {
            readCapsLock(event)
            onKeyUp?.(event)
          }}
          onBlur={(event) => {
            // Leaving the field ends the warning's usefulness; keeping it up
            // next to an unfocused box just reads as an error.
            setCapsLock(false)
            onBlur?.(event)
          }}
        />

        <button
          // type=button, or this submits the form it sits in. The default type
          // for a <button> inside a <form> is "submit".
          type="button"
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? 'Sakrij lozinku' : 'Prikaži lozinku'}
          aria-pressed={visible}
          // Not aria-hidden and not tabIndex={-1}: someone on a keyboard has
          // the same reason to check what they typed as someone on a phone.
          className={cn(
            'text-muted-foreground hover:text-foreground absolute top-1/2 right-1 -translate-y-1/2',
            // size-11, not size-10. This file sets the 44px rule for every other
            // control and then missed it here by 4px — on the one button a
            // worker in gloves presses when the password they typed is wrong.
            'flex size-11 items-center justify-center rounded-md transition-colors',
            'focus-visible:ring-ring/50 focus-visible:ring-3 focus-visible:outline-none',
          )}
        >
          {visible ? <EyeOff className="size-4.5" /> : <Eye className="size-4.5" />}
        </button>
      </div>

      {capsLock && (
        <span
          role="status"
          className="mt-1 flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-400"
        >
          <TriangleAlert className="size-3.5 shrink-0" aria-hidden />
          Caps Lock je uključen
        </span>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Number
// ---------------------------------------------------------------------------

/**
 * A number box that does not change its value when the page scrolls.
 *
 * `<input type="number">` has a long-standing browser behaviour: while it has
 * focus, the mouse wheel increments and decrements it. A manager who tapped
 * into "Bodovi", then scrolled down to read the rest of the review, would
 * silently award a different number of points than they typed — with no
 * indication anything had happened. The same applies to the money fields.
 *
 * Blurring on wheel is the fix that actually works. `preventDefault` does not:
 * React attaches wheel listeners passively, so the call is ignored.
 *
 * The spin buttons are hidden too. They are about 10px wide, which is not a
 * realistic target on a phone, and they crowd the value on desktop.
 */
export function NumberInput({ className, onWheel, inputMode, ...props }: ComponentProps<typeof ShadInput>) {
  return (
    <Input
      {...props}
      type="number"
      inputMode={inputMode ?? 'decimal'}
      onWheel={(event) => {
        if (document.activeElement === event.currentTarget) {
          event.currentTarget.blur()
        }
        onWheel?.(event)
      }}
      className={cn(
        '[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
        '[-moz-appearance:textfield]',
        className,
      )}
    />
  )
}

// ---------------------------------------------------------------------------
// Date
// ---------------------------------------------------------------------------

/** Today, as the `yyyy-mm-dd` a date input expects — in local time, not UTC. */
export function todayISO(): string {
  const now = new Date()
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000)

  return local.toISOString().slice(0, 10)
}

/** `todayISO()` shifted by a number of whole years. */
export function yearsFromToday(years: number): string {
  const date = new Date()
  date.setFullYear(date.getFullYear() + years)
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000)

  return local.toISOString().slice(0, 10)
}

/**
 * A native date input with both ends bounded.
 *
 * The native control is deliberate — it is the one piece of date UI that is
 * already translated, already keyboard accessible, and on a phone opens the
 * OS picker rather than a floating grid of 8mm targets. A JS calendar would be
 * a downgrade here, and shadcn's Calendar in particular drags in
 * react-day-picker plus a Popover that has its own focus problems when opened
 * from inside a Dialog — which is exactly where these fields live.
 *
 * What the native control does badly is typing. The year segment accepts six
 * digits, so a slipped keystroke gives a task a due date in the year 202500 and
 * the field still reads as valid. Both ends are therefore always bounded; the
 * defaults are wide enough never to be felt and narrow enough to catch a typo.
 */
export function DateInput({
  className,
  min = yearsFromToday(-5),
  max = yearsFromToday(5),
  ...props
}: ComponentProps<typeof ShadInput>) {
  return <Input {...props} type="date" min={min} max={max} className={className} />
}

/**
 * A clock time, `HH:MM`.
 *
 * The native control again, and for the same reasons as the date above: it is
 * the picker a phone already knows how to show, and on a factory phone that
 * matters more than anything a library would draw.
 *
 * No bounds to set — every value a time field accepts is a real time — so
 * unlike `DateInput` this is a plain pass-through, and exists so that a screen
 * asking for a time does not have to remember the `type`.
 */
export function TimeInput({ className, ...props }: ComponentProps<typeof ShadInput>) {
  return <Input {...props} type="time" className={className} />
}
